import type { Dispatch } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { buildMessagesUrl } from '../sse/buildMessagesUrl';
import type {
  SendMessageRequest,
  SendMessageResponse,
} from '../sse/publishStreamingMessage';
import { parseSendMessageResponse } from '../sse/sendMessageResponse';
import {
  fetchWithSidekickAuth,
  SidekickHttpError,
  withSidekickTransportTimeout,
} from '../sse/sidekickHttp';
import { setIsStreaming } from '../state/aiSidekickChatSlice';
import type { SidekickError } from '../state/chatTypes';
import { SYNTHETIC_TRANSPORT_ERROR_CODE } from '../state/constants';
import { applySendMessageResponseToSlice } from './syncResponseMappers';

/** Params accepted by {@link useSidekickSyncResponse}. */
export type UseSidekickSyncResponseParams = {
  /**
   * Active conversation id, or `null` when the panel has not yet
   * opened a conversation. `send()` rejects when `null`.
   */
  conversationId: string | null;
  /**
   * Host-supplied Sidekick API base URL (e.g.
   * `${globalThis.appConfig.apiBaseUrl}/sidekick` in `mf-insights`).
   * The hook appends `/conversations/{id}/messages`.
   */
  apiBaseUrl: string;
  /**
   * Called when the BE returns a non-2xx response with a typed error
   * body, or when a transport-level error fires. The orchestrator
   * wraps the typed payload into an inline `AgentErrorCard` via
   * `addErrorEvent` with the source user-message id. Return `true`
   * when another attempt was scheduled and the waiting state must
   * remain active.
   */
  onError?: (error: SidekickError) => boolean | undefined;
  /**
   * Called after the BE returns a 2xx response and the assistant
   * message has been dispatched to the slice. Receives the trace id,
   * or an empty string when the response omits one.
   */
  onDone?: (traceId: string) => void;
};

/** Caller-supplied input for a single sync turn. */
export type SendSyncMessageInput = {
  /** Plain text content the user submitted. */
  content: string;
  /** Conversation id for this turn; defaults to the hook param. */
  conversationId?: string;
  /** Input modality — defaults to `'text'`. */
  inputType?: 'text' | 'voice';
  modelHint?: string;
  promptAlias?: string;
  interface?: string;
  fleetId?: string;
};

/** Imperative API returned by {@link useSidekickSyncResponse}. */
export type UseSidekickSyncResponseResult = {
  /**
   * Fires the POST and resolves once the slice has been updated
   * (success path) or the error callback has fired (failure path).
   * Throws only on programmer error (e.g. null conversation id);
   * all network / BE errors surface via `onError`.
   */
  send: (input: SendSyncMessageInput) => Promise<void>;
  /** Cancels any in-flight POST. Safe to call when idle. */
  abort: () => void;
};

const clearSyncLifecycleFlags = (dispatch: Dispatch): void => {
  dispatch(setIsStreaming(false));
};

const buildRequestBody = (input: SendSyncMessageInput): string => {
  const body: SendMessageRequest = {
    content: input.content,
    inputType: input.inputType ?? 'text',
    ...(input.modelHint !== undefined ? { modelHint: input.modelHint } : {}),
    ...(input.promptAlias !== undefined
      ? { promptAlias: input.promptAlias }
      : {}),
    ...(input.interface !== undefined ? { interface: input.interface } : {}),
    ...(input.fleetId !== undefined ? { fleetId: input.fleetId } : {}),
  };
  return JSON.stringify(body);
};

/**
 * POSTs the user turn synchronously and dispatches the finalized
 * assistant `Message` + trace id into `aiSidekickChatSlice`. This is
 * the LD-flag-off path for SDPCDXP-1499 — used when the SSE
 * kill-switch is engaged (UAT enablement gate, post-incident
 * rollback) and as the fallback whenever the SSE infrastructure is
 * unavailable.
 *
 * Auth, 401-once refresh, and 409-once retry are delegated to
 * {@link fetchWithSidekickAuth}. The JSON body is runtime-guarded via
 * {@link parseSendMessageResponse}; slice updates go through
 * {@link applySendMessageResponseToSlice}.
 */
export const useSidekickSyncResponse = (
  params: UseSidekickSyncResponseParams
): UseSidekickSyncResponseResult => {
  const { conversationId, apiBaseUrl, onError, onDone } = params;
  const dispatch = useDispatch();

  const abortRef = useRef<AbortController | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const abort = useCallback((): void => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearSyncLifecycleFlags(dispatch);
  }, [dispatch]);

  const send = useCallback(
    async (input: SendSyncMessageInput): Promise<void> => {
      const targetConversationId = input.conversationId ?? conversationId;
      if (targetConversationId === null) {
        throw new Error('Sidekick sync: cannot send without conversationId');
      }
      abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const url = buildMessagesUrl(apiBaseUrl, targetConversationId);
      dispatch(setIsStreaming(true));
      const isCurrentRequest = (): boolean =>
        abortRef.current === controller && !controller.signal.aborted;
      const reportError = (error: SidekickError): void => {
        if (
          abortRef.current === controller &&
          onErrorRef.current?.(error) !== true
        ) {
          clearSyncLifecycleFlags(dispatch);
        }
      };

      try {
        const response = await fetchWithSidekickAuth({
          url,
          signal: controller.signal,
          init: {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: buildRequestBody(input),
          },
        });

        let payload: SendMessageResponse;
        try {
          const parsed = await withSidekickTransportTimeout({
            operation: response.json(),
            message: 'Sidekick sync: SendMessageResponse JSON timed out',
            onTimeout: () => {
              controller.abort();
            },
          });
          payload = parseSendMessageResponse(parsed);
        } catch (parseErr) {
          if (controller.signal.aborted && abortRef.current !== controller) {
            return;
          }
          if (parseErr instanceof SidekickHttpError) {
            reportError(parseErr.sidekickError);
          } else {
            reportError({
              code: SYNTHETIC_TRANSPORT_ERROR_CODE,
              message:
                parseErr instanceof Error ? parseErr.message : String(parseErr),
            });
          }
          return;
        }

        if (!isCurrentRequest()) {
          return;
        }
        if (payload.assistantMessage === undefined) {
          reportError({
            code: SYNTHETIC_TRANSPORT_ERROR_CODE,
            message: 'Sidekick sync: response missing assistantMessage payload',
          });
          return;
        }

        applySendMessageResponseToSlice(dispatch, payload);
        const traceId = payload.traceId ?? '';
        onDoneRef.current?.(traceId);
      } catch (err) {
        if (controller.signal.aborted && abortRef.current !== controller) {
          return;
        }
        if (err instanceof SidekickHttpError) {
          reportError(err.sidekickError);
        } else {
          reportError({
            code: SYNTHETIC_TRANSPORT_ERROR_CODE,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [conversationId, apiBaseUrl, dispatch, abort]
  );

  useEffect(
    () => () => {
      abortRef.current?.abort();
      abortRef.current = null;
      clearSyncLifecycleFlags(dispatch);
    },
    [dispatch]
  );

  return useMemo(() => ({ send, abort }), [send, abort]);
};
